import React, { Component, ChangeEvent, FormEvent, FocusEvent } from 'react'
import { Link, withRouter, useHistory } from 'react-router-dom'
import classnames from 'classnames'
import { withFirebase } from '../../components/Firebase'
import * as ROUTES from '../../constants/routes'
import { safeMigration } from '../../config/featureFlags'
import { setDoc } from 'firebase/firestore'
import VaultLogo from '../../components/global/VaultLogo'
import {
    Button,
    Card,
    CardHeader,
    Form,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Input,
    CardBody,
    Container,
    Row,
    Col,
} from "reactstrap";
const SignUp = () => {
    const history = useHistory();
    
    const handleClose = () => {
        history.push(ROUTES.SIGN_IN);
    };

    return (
        <Container>
            <div className="wrapper">
                <Row>
                    <Col className="mx-auto" lg="5" md="12">
                        <VaultLogo />
                        <Card className="text-center modal-style">
                            <button onClick={handleClose} className="close-button">
                                ×
                            </button>
                            <CardHeader>
                                <h4 className="modal-title">Sign Up</h4>
                            </CardHeader>
                            <SignUpForm />
                        </Card>
                    </Col>
                </Row>
            </div>
        </Container>
    );
};


const INITIAL_STATE = {
    username: '',
    email: '',
    passwordOne: '',
    passwordTwo: '',
    usernameFocus: false,
    emailFocus: false,
    passwordOneFocus: false,
    passwordTwoFocus: false,
    error: null,
};

type SignUpFormState = {
    username: string
    email: string
    passwordOne: string
    passwordTwo: string
    usernameFocus: boolean
    emailFocus: boolean
    passwordOneFocus: boolean
    passwordTwoFocus: boolean
    error: null | any
};

type SignUpFormProps = {
    firebase: any
    history: any
};
class SignUpFormBase extends Component<SignUpFormProps, SignUpFormState> {

    constructor(props: SignUpFormProps) {
        super(props);
        this.state = { ...INITIAL_STATE }
    }

    onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const { username, email, passwordOne } = this.state
        let roles: any = {}

        try {
            const authUser = await safeMigration(
                'useAuthHooks',
                // New hook method - using original for now as this is a class component
                async () => {
                    return await this.props.firebase.doCreateUserWithEmailAndPassword(email, passwordOne);
                },
                // Original method (fallback)
                async () => {
                    return await this.props.firebase.doCreateUserWithEmailAndPassword(email, passwordOne);
                },
                'SignUp-doCreateUserWithEmailAndPassword'
            );

            await safeMigration(
                'useAuthHooks',
                // New hook method - using setDoc with the user document reference
                async () => {
                    const userDocRef = this.props.firebase.user(authUser.user.uid);
                    return await setDoc(userDocRef, {
                        username,
                        email,
                        roles
                    });
                },
                // Original method (fallback) - also using setDoc
                async () => {
                    const userDocRef = this.props.firebase.user(authUser.user.uid);
                    return await setDoc(userDocRef, {
                        username,
                        email,
                        roles
                    });
                },
                'SignUp-createUserDocument'
            );

            this.setState({ ...INITIAL_STATE })

            // Check if there's a pending invite code from the invite flow
            const pendingInviteCode = sessionStorage.getItem('pendingInviteCode');
            if (pendingInviteCode) {
                // Clear the stored code and redirect back to the invite page
                sessionStorage.removeItem('pendingInviteCode');
                this.props.history.push(`/invite/${pendingInviteCode}`);
            } else {
                // Check if there's a pending claim code from the license claim flow
                const pendingClaimCode = sessionStorage.getItem('pendingClaimCode');
                if (pendingClaimCode) {
                    // New user needs to create an org first, then they'll claim
                    // Keep the claim code in session, it will be handled after org creation
                    this.props.history.push(ROUTES.GET_STARTED);
                } else {
                    // New users have no organizations, send them to create/join one
                    this.props.history.push(ROUTES.GET_STARTED);
                }
            }
        } catch (error: any) {
            this.setState({ error })
        }
    }

    onChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ [event.target.name]: event.target.value } as any);
    }

    render() {
        const {
            username,
            email,
            passwordOne,
            passwordTwo,
            usernameFocus,
            emailFocus,
            passwordOneFocus,
            passwordTwoFocus,
            error,
        } = this.state;

        const isInvalid =
            passwordOne !== passwordTwo ||
            passwordOne === '' ||
            email === '' ||
            username === '';

        return (
            <Form className="form" onSubmit={this.onSubmit}>
                <CardBody>
                    <InputGroup
                        className={classnames({
                            "input-group-focus": usernameFocus
                        })}
                    >
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="tim-icons icon-single-02" />
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="User name"
                            name="username"
                            type="text"
                            value={username}
                            onChange={this.onChange}
                            onFocus={(event: FocusEvent<HTMLInputElement>) =>
                                this.setState({ usernameFocus: true })
                            }
                            onBlur={(event: FocusEvent<HTMLInputElement>) =>
                                this.setState({ usernameFocus: false })
                            }
                        />
                    </InputGroup>
                    <InputGroup
                        className={classnames({
                            "input-group-focus": emailFocus
                        })}
                    >
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="tim-icons icon-email-85" />
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Email"
                            name="email"
                            type="text"
                            value={email}
                            onChange={this.onChange}
                            onFocus={() =>
                                this.setState({ emailFocus: true })
                            }
                            onBlur={() =>
                                this.setState({ emailFocus: false })
                            }
                        />
                    </InputGroup>
                    <InputGroup
                        className={classnames({
                            "input-group-focus": passwordOneFocus
                        })}
                    >
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="tim-icons icon-lock-circle" />
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Password"
                            name="passwordOne"
                            type="password"
                            value={passwordOne}
                            onChange={this.onChange}
                            onFocus={() =>
                                this.setState({ passwordOneFocus: true })
                            }
                            onBlur={() =>
                                this.setState({ passwordOneFocus: false })
                            }
                        />
                    </InputGroup>
                    <InputGroup
                        className={classnames({
                            "input-group-focus": passwordTwoFocus
                        })}
                    >
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="tim-icons icon-lock-circle" />
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Confirm password"
                            name="passwordTwo"
                            type="password"
                            value={passwordTwo}
                            onChange={this.onChange}
                            onFocus={() =>
                                this.setState({ passwordTwoFocus: true })
                            }
                            onBlur={() =>
                                this.setState({ passwordTwoFocus: false })
                            }
                        />
                    </InputGroup>
                    <Button
                        disabled={isInvalid}
                        className="btn-info-custom"
                        size="lg"
                        type="submit"
                    >
                        <span>Sign Up</span>
                    </Button>
                    {error && <p className="text-warning pt-2">{error.message}</p>}
                </CardBody>
            </Form>
        );
    }
}
const SignUpLink = () => (
    <p className="px-2 text-center">
        Don't have an account? <Link to={ROUTES.SIGN_UP} className="accent-link">Sign Up</Link>
    </p>
);

const SignUpForm = withRouter(withFirebase(SignUpFormBase))

export default SignUp
export { SignUpForm, SignUpLink }